import {
  Badge, Button, Group, Modal, Paper, Select, Stack, Text, Textarea, TextInput, ThemeIcon, Timeline, Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconArrowBackUp, IconCheck, IconCircleCheck, IconClockPlay, IconFilePlus, IconLock, IconMessage, IconPaperclip,
  IconPencil, IconRefresh, IconSend, IconUserCheck, IconX,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Map as OlMap, View } from "ol";
import GeoJSON from "ol/format/GeoJSON";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import OSM from "ol/source/OSM";
import VectorSource from "ol/source/Vector";
import { Fill, Stroke, Style, Text as OlText } from "ol/style";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { downloadFile, http } from "../../api/http";
import { fmtDate, fmtDateTime } from "../../components/ui";
import { useT } from "../../i18n/script";

// ============================================================================== tipovi
export interface ParcelIn { ko_id: number | null; ko_name: string | null; number: string; parcel_id?: number | null; use?: string | null }
export interface OrchardIn {
  name?: string | null; species: string; cultivar?: string | null; rootstock?: string | null; planting_year?: number | null;
  row_spacing_m?: number | null; tree_spacing_m?: number | null; trees?: number | null; organic: boolean; hail_net: boolean;
  irrigation?: string | null; parcels: string[];
}
export interface CaseData {
  bpg?: string | null; holding_name?: string | null; holder_name?: string | null;
  holding_type: "fizicko_lice" | "pravno_lice" | "preduzetnik"; jib?: string | null;
  relation: "nosilac" | "clan" | "ovlasceno_lice"; opstina_code?: string | null; address?: string | null;
  phone?: string | null; email?: string | null; parcels: ParcelIn[]; orchards: OrchardIn[]; message?: string | null; consent: boolean;
}
export interface CaseEvent {
  id?: number; ts: string; actor?: string; actor_name: string; kind: string; note?: string | null;
  data?: Record<string, unknown>; visible_to_applicant?: boolean;
}
export interface CaseDoc {
  id: number; reg_no?: string; title: string; created_at: string; created_by?: string; filename?: string; mime_type?: string; size_bytes?: number;
}
export interface ParcelLookup {
  found: boolean; id?: number; ko_name?: string; label?: string; area_m2?: number; land_use?: string | null; geojson?: unknown; koristi?: string | null;
}
export interface Registry {
  holding: { id: number; bpg: string; name: string; holder_name: string | null; rpg_status: string | null; verification: string | null; opstina: string | null } | null;
  holder_match?: boolean;
  parcels: (ParcelIn & { found: boolean; cadastre: ParcelLookup | null })[];
}
export interface CheckState { ok: boolean | null; note?: string | null; by?: string; at?: string }
export interface CaseDetail {
  id: number; number: string; kind: string; kind_label: string; status: string; status_label: string; priority?: string;
  applicant_sub?: string; applicant_username?: string; applicant_name?: string | null; applicant_email?: string | null; applicant_phone?: string | null;
  bpg?: string | null; holding_id?: number | null; data: CaseData; checklist: Record<string, CheckState> | null;
  assigned_to?: string | null; assigned_name?: string | null; assigned_by?: string | null; assigned_at?: string | null; due_date?: string | null;
  decided_by?: string | null; decided_at?: string | null; decision_note?: string | null; submitted_at?: string | null;
  created_at: string; updated_at: string; opstina?: string | null; opstina_code?: string | null;
  events: CaseEvent[]; documents: CaseDoc[]; overdue: boolean; checklist_items: string[];
  can_assign: boolean; can_process: boolean; can_edit: boolean; registry?: Registry;
}
export interface StaffUser { username: string; name: string; email?: string; roles: string[]; opstine: string[]; open_cases: number }

export const OPEN_STATUSES = ["podnesen", "dodijeljen", "u_obradi", "dopuna"];

// ============================================================================== status
export const CASE_STATUS: Record<string, { color: string; label: string; short: string }> = {
  nacrt: { color: "gray", label: "Nacrt", short: "Nacrt" },
  podnesen: { color: "yellow", label: "Podnesen — čeka dodjelu", short: "Čeka dodjelu" },
  dodijeljen: { color: "blue", label: "Dodijeljen referentu", short: "Dodijeljen" },
  u_obradi: { color: "indigo", label: "U obradi", short: "U obradi" },
  dopuna: { color: "orange", label: "Vraćen na dopunu", short: "Na dopuni" },
  odobren: { color: "green", label: "Odobren — gazdinstvo aktivno", short: "Odobren" },
  odbijen: { color: "red", label: "Odbijen", short: "Odbijen" },
};

export function CaseStatusBadge({ status, size = "sm", long = false, variant = "light" }: {
  status?: string | null; size?: string; long?: boolean; variant?: "light" | "filled" | "white" | "outline" | "dot";
}) {
  const { t } = useT();
  if (!status) return null;
  const m = CASE_STATUS[status] ?? { color: "gray", label: status, short: status };
  return <Badge color={m.color} variant={variant} size={size} radius="sm">{t(long ? m.label : m.short)}</Badge>;
}

// ============================================================================== istorija
const EVENT: Record<string, { label: string; color: string; icon: ReactNode }> = {
  kreiran: { label: "Nacrt kreiran", color: "gray", icon: <IconPencil size={13} /> },
  podnesen: { label: "Poslat na verifikaciju", color: "agri", icon: <IconSend size={13} /> },
  dodijeljen: { label: "Dodijeljen referentu", color: "blue", icon: <IconUserCheck size={13} /> },
  u_obradi: { label: "Provjera u toku", color: "indigo", icon: <IconClockPlay size={13} /> },
  dopuna: { label: "Vraćen na dopunu", color: "orange", icon: <IconArrowBackUp size={13} /> },
  dopunjen: { label: "Dopunjen i ponovo poslat", color: "agri", icon: <IconRefresh size={13} /> },
  prilog: { label: "Priložen dokument", color: "gray", icon: <IconPaperclip size={13} /> },
  komentar: { label: "Poruka", color: "teal", icon: <IconMessage size={13} /> },
  odobren: { label: "Odobren — gazdinstvo verifikovano", color: "green", icon: <IconCircleCheck size={13} /> },
  odbijen: { label: "Odbijen", color: "red", icon: <IconX size={13} /> },
};

export function CaseTimeline({ events, compact = false }: { events: CaseEvent[]; compact?: boolean }) {
  const { t } = useT();
  if (!events.length) return <Text size="sm" c="dimmed">{t("Nema događaja")}</Text>;
  const list = [...events].reverse();
  return (
    <Timeline active={list.length} bulletSize={24} lineWidth={2} color="agri">
      {list.map((e, i) => {
        const m = EVENT[e.kind] ?? { label: e.kind, color: "gray", icon: <IconFilePlus size={13} /> };
        const internal = e.visible_to_applicant === false;
        const rok = e.kind === "dodijeljen" && e.data?.rok ? String(e.data.rok) : null;
        return (
          <Timeline.Item key={e.id ?? `${e.ts}-${i}`}
            bullet={<ThemeIcon size={24} radius="xl" color={internal ? "gray" : m.color} variant={i === 0 ? "filled" : "light"}>
              {internal ? <IconLock size={13} /> : m.icon}</ThemeIcon>}
            title={<Group gap={6} wrap="wrap">
              <Text size="sm" fw={700}>{t(m.label)}</Text>
              {internal && <Badge size="xs" color="gray" variant="outline">{t("interno")}</Badge>}
            </Group>}>
            {e.note && e.kind !== "prilog" && (
              <Text size="sm" mt={2} style={{ whiteSpace: "pre-wrap" }} lineClamp={compact ? 3 : undefined}>{e.note}</Text>
            )}
            {e.kind === "prilog" && e.note && <Text size="sm" mt={2} c="dimmed">{e.note}</Text>}
            {e.kind === "dodijeljen" && e.data?.referent != null && (
              <Text size="xs" c="dimmed" mt={2}>{t("Referent")}: {String(e.data.referent)}{rok ? ` · ${t("rok")} ${fmtDate(rok)}` : ""}</Text>
            )}
            <Text size="xs" c="dimmed" mt={2}>{e.actor_name} · {fmtDateTime(e.ts)}</Text>
          </Timeline.Item>
        );
      })}
    </Timeline>
  );
}

/** Napredak predmeta u 4 koraka (za podnosioca i zaglavlje). */
export function CaseProgress({ status }: { status: string }) {
  const { t } = useT();
  const steps = ["Poslat", "Dodijeljen", "Provjera", "Odluka"];
  const idx = { nacrt: -1, podnesen: 0, dodijeljen: 1, u_obradi: 2, dopuna: 2, odobren: 3, odbijen: 3 }[status] ?? 0;
  const endColor = status === "odbijen" ? "#ff8a80" : "#b6f09c";
  return (
    <Group gap={6} wrap="nowrap" mt="sm" style={{ maxWidth: 520 }}>
      {steps.map((s, i) => (
        <div key={s} style={{ flex: 1, minWidth: 0 }}>
          <div style={{ height: 5, borderRadius: 3, background: i <= idx ? (i === 3 ? endColor : "#b6f09c") : "rgba(255,255,255,.22)",
            transition: "background .3s" }} />
          <Text size="xs" mt={4} c={i <= idx ? "white" : "rgba(255,255,255,.6)"} fw={i === idx ? 700 : 500} truncate>
            {t(i === 2 && status === "dopuna" ? "Dopuna" : s)}
          </Text>
        </div>
      ))}
    </Group>
  );
}

// ============================================================================== stilovi (lokalni za modul)
export const CASES_CSS = `
.case-kpi { cursor: pointer; transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease, background .15s ease; border-left-width: 4px !important; }
.case-kpi:hover { transform: translateY(-2px); box-shadow: 0 8px 22px rgba(31,77,54,.12); }
.case-kpi[data-active="true"] { background: var(--mantine-color-agri-0); border-color: var(--mantine-color-agri-6) !important; }
[data-mantine-color-scheme="dark"] .case-kpi[data-active="true"] { background: var(--mantine-color-dark-5); }
.case-row { cursor: pointer; transition: background .12s ease; }
.case-row:hover > td { background: var(--mantine-color-agri-0); }
[data-mantine-color-scheme="dark"] .case-row:hover > td { background: var(--mantine-color-dark-5); }
.case-row:focus-visible { outline: 2px solid var(--agri-apple); outline-offset: -2px; }
.case-hero { position: relative; overflow: hidden; border-radius: 14px; color: #fff; padding: 22px 24px;
  background: radial-gradient(circle at 92% 10%, rgba(182,240,156,.22) 0, transparent 38%), linear-gradient(130deg, #173d2a 0%, #1f4d36 45%, #2f6b47 100%); }
.case-hero .apple-dot { position: absolute; right: -40px; bottom: -60px; width: 220px; height: 220px; border-radius: 50%;
  background: radial-gradient(circle at 35% 35%, rgba(200,69,58,.55), rgba(200,69,58,.08) 70%); pointer-events: none; }
.case-sticky { position: sticky; top: calc(var(--header-h) + 12px); }
@media (max-width: 991px) { .case-sticky { position: static; } }
.case-bubble { padding: 8px 12px; border-radius: 12px; max-width: 85%; white-space: pre-wrap; }
.case-bubble.mine { background: var(--mantine-color-agri-8); color: #fff; margin-left: auto; border-bottom-right-radius: 4px; }
.case-bubble.theirs { background: #eef3ec; border-bottom-left-radius: 4px; }
[data-mantine-color-scheme="dark"] .case-bubble.theirs { background: var(--mantine-color-dark-5); }
.case-check { transition: background .15s ease; border-radius: 10px; }
.case-check[data-state="ok"] { background: rgba(64,192,87,.08); }
.case-check[data-state="bad"] { background: rgba(250,82,82,.08); }
.case-fadein { animation: caseFade .3s ease-out; }
@keyframes caseFade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .case-kpi, .case-row, .case-fadein { transition: none; animation: none; } }
`;
export const CasesStyle = () => <style>{CASES_CSS}</style>;

// ============================================================================== mapa čestica
/** Mali prikaz čestica (OSM podloga, EPSG:3857; geometrije u EPSG:4326). */
export function ParcelMap({ items, height = 260 }: { items: { geojson: unknown; label?: string; warn?: boolean }[]; height?: number }) {
  const el = useRef<HTMLDivElement>(null);
  const mapRef = useRef<OlMap | null>(null);
  const srcRef = useRef<VectorSource | null>(null);
  useEffect(() => {
    if (!el.current) return;
    const src = new VectorSource();
    srcRef.current = src;
    const map = new OlMap({
      target: el.current,
      layers: [
        new TileLayer({ source: new OSM() }),
        new VectorLayer({ source: src, style: (f) => new Style({
          stroke: new Stroke({ color: f.get("warn") ? "#e8590c" : "#c8453a", width: 2.5 }),
          fill: new Fill({ color: f.get("warn") ? "rgba(232,89,12,.18)" : "rgba(200,69,58,.16)" }),
          text: new OlText({ text: f.get("label") ?? "", font: "600 12px Manrope Variable, sans-serif", fill: new Fill({ color: "#18261e" }),
            stroke: new Stroke({ color: "#fff", width: 3 }), overflow: true }),
        }) }),
      ],
      view: new View({ center: [1890000, 5560000], zoom: 8 }),
    });
    mapRef.current = map;
    return () => { map.setTarget(undefined); mapRef.current = null; };
  }, []);
  const key = JSON.stringify(items.map((i) => [i.label, i.warn, !!i.geojson]));
  useEffect(() => {
    const src = srcRef.current, map = mapRef.current;
    if (!src || !map) return;
    src.clear();
    const fmt = new GeoJSON();
    for (const it of items) {
      if (!it.geojson) continue;
      try {
        const fs = fmt.readFeatures({ type: "Feature", geometry: it.geojson, properties: { label: it.label, warn: !!it.warn } },
          { dataProjection: "EPSG:4326", featureProjection: "EPSG:3857" });
        src.addFeatures(fs);
      } catch { /* neispravna geometrija */ }
    }
    const ext = src.getExtent();
    if (src.getFeatures().length && ext && Number.isFinite(ext[0])) map.getView().fit(ext, { padding: [28, 28, 28, 28], maxZoom: 18, duration: 250 });
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return <div ref={el} style={{ height, width: "100%", borderRadius: 12, overflow: "hidden", border: "1px solid #d5ded3" }} />;
}

// ============================================================================== šifarnici i pomoćne funkcije
export interface KoItem { id: number; name: string; opstina_code: string | null; opstina: string | null }

/** Katastarske opštine (sve) i opštine izvedene iz njih (+ admin-units ako ih korisnik vidi). */
export function useKoAndMunicipalities() {
  const ko = useQuery({ queryKey: ["cases-ko-all"], staleTime: 10 * 60_000, queryFn: () => http.get<KoItem[]>("/api/v1/cases/lookup/ko") });
  const units = useQuery({ queryKey: ["admin-units-header"], staleTime: 5 * 60_000,
    queryFn: () => http.get<{ code: string; name: string }[]>("/api/v1/admin-units") });
  const municipalities = useMemo(() => {
    const m = new Map<string, string>();
    for (const k of ko.data ?? []) if (k.opstina_code) m.set(k.opstina_code, k.opstina ?? k.opstina_code);
    for (const u of units.data ?? []) if (!m.has(u.code)) m.set(u.code, u.name);
    return [...m.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, "sr"));
  }, [ko.data, units.data]);
  return { ko: ko.data ?? [], municipalities, loading: ko.isLoading };
}

export function useSpeciesOptions() {
  const q = useQuery({ queryKey: ["vrsta_voca"], staleTime: 600_000,
    queryFn: () => http.get<{ code: string; name: string; active?: boolean }[]>("/api/v1/codelists/vrsta_voca/items") });
  return (q.data ?? []).filter((x) => x.active !== false).map((x) => ({ value: x.code, label: x.name }));
}

/** Provjera čestice u katastru. Čestica bez podbroja se vodi kao „broj/0“ — ako „1234“ nije nađena, pokušava se „1234/0“. */
export async function lookupParcel(koId: number, number: string): Promise<ParcelLookup & { number: string }> {
  const n = number.trim().replace(/\s+/g, "");
  const r = await http.get<ParcelLookup>("/api/v1/cases/lookup/parcel", { ko_id: koId, number: n });
  if (r.found || n.includes("/")) return { ...r, number: n };
  const r2 = await http.get<ParcelLookup>("/api/v1/cases/lookup/parcel", { ko_id: koId, number: `${n}/0` });
  return r2.found ? { ...r2, number: `${n}/0` } : { ...r, number: n };
}

export const fmtArea = (m2?: number | null) =>
  m2 == null ? "—" : m2 >= 10000 ? `${(m2 / 10000).toLocaleString("sr-Latn-BA", { maximumFractionDigits: 2 })} ha`
    : `${m2.toLocaleString("sr-Latn-BA")} m²`;

export const fmtSize = (b?: number) => b == null ? "" : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;

export const HOLDING_TYPE: Record<string, string> = { fizicko_lice: "Fizičko lice", pravno_lice: "Pravno lice", preduzetnik: "Preduzetnik" };
export const RELATION: Record<string, string> = { nosilac: "Nosilac gazdinstva", clan: "Član gazdinstva", ovlasceno_lice: "Ovlašćeno lice" };
export const PARCEL_USE: Record<string, string> = { vocnjak: "Voćnjak", oranica: "Oranica", livada: "Livada", ostalo: "Ostalo" };

export const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));

export async function openDocument(caseId: number, d: CaseDoc, onError: (m: string) => void) {
  try {
    await downloadFile(`/api/v1/cases/${caseId}/documents/${d.id}/file?inline=true`, {}, true);
  } catch (e) { onError(errMsg(e)); }
}

/** Broj dana do roka (negativno = kasni). */
export function daysLeft(due?: string | null): number | null {
  if (!due) return null;
  const d = new Date(due + (due.length === 10 ? "T00:00:00" : ""));
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86_400_000);
}

export function DueText({ due, overdue, closed }: { due?: string | null; overdue?: boolean | null; closed?: boolean }) {
  const { t } = useT();
  if (!due) return <Text size="sm" c="dimmed">—</Text>;
  const dl = daysLeft(due);
  const late = !!overdue || (!closed && dl != null && dl < 0);
  return (
    <Tooltip label={late ? t(`Kasni ${Math.abs(dl ?? 0)} d.`) : dl != null && !closed ? t(`Još ${dl} d.`) : fmtDate(due)} withArrow>
      <Text size="sm" fw={late ? 700 : 500} c={late ? "red.7" : !closed && dl != null && dl <= 2 ? "orange.7" : undefined}>
        {fmtDate(due)}
      </Text>
    </Tooltip>
  );
}

// ============================================================================== dodjela predmeta
export function AssignModal({ caseId, caseNumber, current, opened, onClose, onDone }: {
  caseId: number | null; caseNumber?: string; current?: string | null; opened: boolean; onClose: () => void; onDone: () => void;
}) {
  const { t } = useT();
  const staff = useQuery({ queryKey: ["cases-staff"], enabled: opened, queryFn: () => http.get<StaffUser[]>("/api/v1/cases/staff") });
  const [user, setUser] = useState<string | null>(null);
  const [due, setDue] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (opened) { setUser(current ?? null); setDue(""); setNote(""); } }, [opened, current]);
  const roleLabel = (r: string[]) => r.map((x) => ({ opstinski_referent: "referent", rukovodilac: "rukovodilac", agronom: "agronom" }[x] ?? x)).join(", ");
  const byName = new Map((staff.data ?? []).map((s) => [s.username, s]));
  const load = (n: number) => n === 0 ? t("bez otvorenih") : n === 1 ? t("1 otvoren") : n < 5 ? t(`${n} otvorena`) : t(`${n} otvorenih`);

  async function save() {
    if (!caseId || !user) return;
    setBusy(true);
    try {
      await http.post(`/api/v1/cases/${caseId}/assign`, { username: user, due_date: due || null, note: note || null });
      notifications.show({ color: "green", icon: <IconCheck size={16} />, message: t(`Predmet je dodijeljen: ${byName.get(user)?.name ?? user}`) });
      onDone(); onClose();
    } catch (e) {
      notifications.show({ color: "red", message: errMsg(e) });
    } finally { setBusy(false); }
  }

  return (
    <Modal opened={opened} onClose={onClose} radius="lg" centered
      title={<Text fw={750}>{t(current ? "Preraspodjela predmeta" : "Dodjela predmeta")}{caseNumber ? ` ${caseNumber}` : ""}</Text>}>
      <Stack gap="sm">
        <Select label={t("Referent")} placeholder={t("Izaberite referenta")} searchable required value={user} onChange={setUser}
          nothingFoundMessage={t("Nema rezultata")}
          data={(staff.data ?? []).map((s) => ({ value: s.username, label: s.name }))}
          renderOption={({ option }) => {
            const s = byName.get(option.value);
            return (
              <Group justify="space-between" wrap="nowrap" w="100%">
                <div style={{ minWidth: 0 }}>
                  <Text size="sm" fw={600} truncate>{option.label}</Text>
                  <Text size="xs" c="dimmed" truncate>{t(roleLabel(s?.roles ?? []))}{s?.opstine?.length ? ` · ${s.opstine.join(", ")}` : ""}</Text>
                </div>
                <Badge size="sm" variant="light" tt="none" color={!s?.open_cases ? "green" : s.open_cases < 5 ? "blue" : "orange"}>{load(s?.open_cases ?? 0)}</Badge>
              </Group>
            );
          }} />
        {user && byName.get(user) && (
          <Text size="xs" c="dimmed">{t("Opterećenje")}: {load(byName.get(user)!.open_cases)}</Text>
        )}
        <TextInput type="date" label={t("Rok za obradu")} value={due} onChange={(e) => setDue(e.currentTarget.value)}
          description={t("Prazno = podrazumijevani rok iz podešavanja sistema")} min={new Date().toISOString().slice(0, 10)} />
        <Textarea label={t("Napomena referentu")} value={note} onChange={(e) => setNote(e.currentTarget.value)} autosize minRows={2} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t("Odustani")}</Button>
          <Button leftSection={<IconUserCheck size={16} />} disabled={!user} loading={busy} onClick={save}>{t("Dodijeli")}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Kartica sa naslovom i ikonom (dosljedan izgled sekcija). */
export function Section({ title, icon, right, children, p = "md" }: { title: string; icon?: ReactNode; right?: ReactNode; children: ReactNode; p?: string }) {
  const { t } = useT();
  return (
    <Paper withBorder radius="lg" p={p} className="case-fadein">
      <Group justify="space-between" mb="sm" wrap="nowrap">
        <Group gap={8} wrap="nowrap">
          {icon && <ThemeIcon variant="light" color="agri" radius="md" size={30}>{icon}</ThemeIcon>}
          <Text fw={750} size="md">{t(title)}</Text>
        </Group>
        {right}
      </Group>
      {children}
    </Paper>
  );
}
